/**
 * streamUtils — 流式数据处理工具模块
 *
 * 提供流式 JSON 解析、文本合并、格式处理等通用函数，
 * 供 App.js、AiHealthReport 等页面使用。
 *
 * @module utils/streamUtils
 */

import iconLjtz from "@/assets/img/icon_ljtz@2x.png";

// ============================================================
// 基础常量
// ============================================================

/** 流程输出节点标识名称 */
const FLOW_OUTPUT_FIELD_NAME = "\u6d41\u7a0b\u8f93\u51fa";

/** 文本提取优先级字段列表 */
const PICK_KEYS = [
  "text",
  "answer",
  "content",
  "data",
  "message",
  "msg",
  "result",
  "output",
  "answer_info",
  "answer_know",
];

/** 流式渲染触发模式枚举 */
export const STREAM_RENDER_TRIGGER_MODES = {
  END_NODE: "endNode",
  FLOW_OUTPUT: "flowOutput",
};

// ============================================================
// JSON 辅助函数
// ============================================================

/**
 * 安全解析 JSON 字符串
 * @param {*} value - 待解析的值（字符串或对象）
 * @param {*} [fallback=null] - 解析失败时的默认值
 * @returns {*} 解析结果或 fallback
 */
export const safeParseJSON = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

// ============================================================
// 流式缓冲区工具
// ============================================================

/**
 * 匹配流式 JSON 缓冲区中从 startIndex 开始的完整花括号块，
 * 正确处理字符串内的转义和嵌套
 * @param {string} str - 缓冲区字符串
 * @param {number} startIndex - 起始 '{' 的位置
 * @returns {number} 匹配的闭合 '}' 位置，-1 表示未完整
 */
export const findMatchingBrace = (str, startIndex) => {
  let depth = 0,
    inString = false,
    escapeNext = false;
  for (let i = startIndex; i < str.length; i++) {
    const char = str[i];
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (char === "\\") {
      escapeNext = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (char === "{") depth++;
    else if (char === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
};

// ============================================================
// 文本提取：pickAnswerText
// ============================================================

/**
 * 递归从各种数据格式中提取可显示文本
 *
 * 支持：字符串、JSON 字符串（自动 parse）、对象（按 PICK_KEYS 优先级查找）、数组
 *
 * @param {*} value - 任意类型的值
 * @returns {string|string[]} 提取到的文本，空字符串表示无内容
 */
export const pickAnswerText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const text = value;
    if (!text || text === "true" || text === "null") return "";
    try {
      return pickAnswerText(JSON.parse(text)) || text;
    } catch {
      return text;
    }
  }
  if (Array.isArray(value)) return value.map(pickAnswerText).filter(Boolean);
  if (typeof value === "object") {
    for (const key of PICK_KEYS) {
      const t = pickAnswerText(value[key]);
      if (t) return t;
    }
  }
  return "";
};

// ============================================================
// 思维节点判断
// ============================================================

/**
 * 判断是否为结束节点
 * @param {object} node - 思维节点对象
 * @returns {boolean}
 */
export const isFinalThoughtNode = (node) => {
  const nodeName = String(node?.nodeName || "").trim();
  const nodeType = String(node?.nodeType || "")
    .trim()
    .toLowerCase();
  return nodeName === "\u7ed3\u675f" || nodeType === "end";
};

/**
 * 判断是否为流程输出节点
 * @param {object} node - 思维节点对象
 * @returns {boolean}
 */
export const isFlowOutputThoughtNode = (node) => {
  const nodeName = String(node?.nodeName || "").trim();
  return nodeName.includes(FLOW_OUTPUT_FIELD_NAME);
};

/**
 * 从思维节点中提取结果文本
 * @param {object} node - 思维节点对象
 * @returns {string}
 */
export const getThoughtNodeResultText = (node) => {
  const nodeResult = safeParseJSON(node?.nodeResult, {});
  return pickAnswerText(nodeResult?.result) || pickAnswerText(nodeResult);
};

// ============================================================
// 思维链文本提取
// ============================================================

/**
 * 从思维链中提取流程输出节点的文本
 * @param {Array} [thoughts=[]] - 思维节点数组
 * @returns {string}
 */
export const getFlowOutputThoughtText = (thoughts = []) => {
  let flowOutputText = "";
  thoughts.forEach((item) => {
    const node = safeParseJSON(item?.response, item);
    if (!isFlowOutputThoughtNode(node)) return;
    const resultText = getThoughtNodeResultText(node);
    if (resultText) flowOutputText = resultText;
  });
  return flowOutputText;
};

/**
 * 从思维链中提取结束节点的文本
 * @param {Array} [thoughts=[]] - 思维节点数组
 * @returns {string}
 */
export const getFinalThoughtText = (thoughts = []) => {
  let finalText = "";
  thoughts.forEach((item) => {
    const node = safeParseJSON(item?.response);
    if (!isFinalThoughtNode(node)) return;
    const resultText = getThoughtNodeResultText(node);
    if (resultText) finalText = resultText;
  });
  return finalText;
};

// ============================================================
// 思维节点信息提取（纯函数）
// ============================================================

/**
 * 从流式数据包的 record 中提取思维节点信息
 *
 * 统一处理 MESSAGE_FORMAT_PLUS 模式和基于 thoughts 的旧模式，
 * 返回可用于更新消息状态的标准结构。
 *
 * @param {object} record - 数据包的 output/record 部分
 * @param {boolean} isMessageFormatPlus - 是否为 MESSAGE_FORMAT_PLUS 模式
 * @param {string} renderTriggerMode - 渲染触发模式 ('endNode' | 'flowOutput')
 * @returns {{ endNodeData: string|null, hasRenderableThoughtText: boolean, shouldFinishByThoughtNode: boolean }}
 */
export const extractThoughtNodeInfo = (
  record,
  isMessageFormatPlus,
  renderTriggerMode,
) => {
  let endNodeData = null;
  let hasRenderableThoughtText = false;
  let shouldFinishByThoughtNode = false;

  if (isMessageFormatPlus) {
    const workflowMessage = record?.workflowMessage;
    if (workflowMessage?.nodeType?.toLowerCase() === "end") {
      endNodeData = pickAnswerText(workflowMessage?.message?.content);
      hasRenderableThoughtText = !!endNodeData;
      shouldFinishByThoughtNode = workflowMessage?.nodeIsCompleted === true;
    }
  } else if (record?.thoughts?.length) {
    if (renderTriggerMode === STREAM_RENDER_TRIGGER_MODES.FLOW_OUTPUT) {
      const flowOutputText = getFlowOutputThoughtText(record.thoughts);
      const finalThoughtText = getFinalThoughtText(record.thoughts);
      if (flowOutputText) {
        endNodeData = flowOutputText;
        hasRenderableThoughtText = true;
      }
      if (finalThoughtText) {
        endNodeData = finalThoughtText;
        hasRenderableThoughtText = true;
        shouldFinishByThoughtNode = true;
      }
    } else {
      const finalThoughtText = getFinalThoughtText(record.thoughts);
      if (finalThoughtText) {
        endNodeData = finalThoughtText;
        hasRenderableThoughtText = true;
        shouldFinishByThoughtNode = true;
      }
    }
  }

  return { endNodeData, hasRenderableThoughtText, shouldFinishByThoughtNode };
};

// ============================================================
// 流式文本合并
// ============================================================

/**
 * 合并流式文本，智能处理重叠部分
 *
 * 当流式接口逐块返回 JSON 数据时，可能出现相邻块
 * 包含重复前缀的情况，此函数自动去除重叠部分。
 *
 * @param {string} [currentText=""] - 当前累积的文本
 * @param {string} [incomingText=""] - 新到达的文本
 * @returns {string} 合并后的文本
 */
export const mergeStreamText = (currentText = "", incomingText = "") => {
  const current = String(currentText || "");
  const incoming = String(incomingText || "");

  if (!incoming) return current;

  return current + incoming;
};

// ============================================================
// 文本格式化（标签解析、Ref 替换等）
// ============================================================

/**
 * 将原始流式文本转换为带图标和胶囊的 HTML
 *
 * 处理 @nutrition、@diagnose、@update、<ref>、<video_id>、
 * <clarify_question>、<clarify_option>、<similar_question>、
 * <guess_question> 等标签。
 *
 * @param {string} fullRawText - 截至目前的完整原始文本
 * @param {object} msg - 当前消息快照
 * @param {boolean} [isFinishReason=false] - 是否为结束标识
 * @returns {object} 处理后的消息对象
 */
export const processTextWithFormats = (
  fullRawText,
  msg,
  isFinishReason = false,
) => {
  let processedMsg = { ...msg };

  let displayContent = fullRawText
    .replace(/[⚠❌]/g, "")
    .replace(/@diagnose/gi, "")
    .replace(/@update/gi, "");

  const nutritionRegex = /@nutrition/gi;

  if (nutritionRegex.test(displayContent)) {
    const nutritionHtml =
      '<div class="nutrition-guide-container"><p class="guide-text">' +
      "为了更好地给孩子定制专属食谱，请告知您孩子的基本信息，" +
      "这样我们可以更精准地设计符合孩子生长需求的营养餐哦~</p>" +
      '<button class="go-fill-nutrition-btn">去填写</button></div>';
    displayContent = displayContent.replace(nutritionRegex, nutritionHtml);
  }

  // --- 1. 视频 ID 处理 ---
  const videoIds = [];
  const videoIdRegex = /<video_id>(.*?)<\/video_id>/gi;
  let vMatch;
  while ((vMatch = videoIdRegex.exec(fullRawText)) !== null) {
    videoIds.push(vMatch[1].trim());
  }

  // --- 2. Ref 标签即时替换 ---
  const refRegex = /<ref>(.*?)<\/ref>/gi;
  displayContent = displayContent.replace(refRegex, (match, content) => {
    const trimmedContent = content.trim();
    const hasChinese = /[\u4e00-\u9fa5]/.test(trimmedContent);

    if (hasChinese) {
      return "";
    } else if (trimmedContent) {
      // 支持 "V0102|G0438" 等多 ID：按 "|" 拆分，每个 ID 渲染一个独立图标
      return trimmedContent
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean)
        .map(
          (id) =>
            '<span class="ref-tag-icon-img" data-id="' +
            id +
            '"><img src="' +
            iconLjtz +
            '" alt="ref" /></span>',
        )
        .join("");
    }
    return "";
  });

  // --- 3. 提取其他标签内容 ---
  const clarifyQuestionRegex = /<clarify_question>(.*?)<\/clarify_question>/gi;
  const clarifyOptionRegex = /<clarify_option>(.*?)<\/clarify_option>/gi;
  const questionTagsRegex = /<(similar_question|guess_question)>(.*?)<\/\1>/gi;
  const endTagRegex = /<end>(.*?)<\/end>/gi;

  let newClarifyOptions = [];
  let extractedQ = "";
  let cqMatch, coMatch, qMatch, endMatch;

  while ((cqMatch = clarifyQuestionRegex.exec(fullRawText)) !== null)
    extractedQ = cqMatch[1].trim();
  while ((coMatch = clarifyOptionRegex.exec(fullRawText)) !== null) {
    if (coMatch[1])
      newClarifyOptions = coMatch[1]
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
  }
  let newQuestions = [];
  while ((qMatch = questionTagsRegex.exec(fullRawText)) !== null) {
    if (qMatch[2])
      newQuestions = [
        ...newQuestions,
        ...qMatch[2]
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean),
      ];
  }
  let extractedEnd = null;
  while ((endMatch = endTagRegex.exec(fullRawText)) !== null) {
    extractedEnd = endMatch[1].trim();
  }

  // --- 4. 清理正文中的 XML 标签，防止 Markdown 渲染乱码 ---
  displayContent = displayContent
    .replace(videoIdRegex, "")
    .replace(clarifyQuestionRegex, "")
    .replace(clarifyOptionRegex, "")
    .replace(questionTagsRegex, "")
    .replace(endTagRegex, "");

  // --- 5. 组合澄清问题 ---
  if (extractedQ && !displayContent.includes(extractedQ)) {
    displayContent = displayContent + "\n" + extractedQ;
  }

  // --- 6. 构建并返回消息对象 ---
  return {
    ...processedMsg,
    text: displayContent,
    similar_questions: Array.from(
      new Set([...(processedMsg.similar_questions || []), ...newQuestions]),
    ),
    clarify_list:
      newClarifyOptions.length > 0
        ? newClarifyOptions
        : processedMsg.clarify_list || [],
    ending: isFinishReason,
    end_data: extractedEnd,
    needTypeWriter: 2,
    file:
      videoIds.length > 0
        ? { type: "video", list: videoIds.map((id) => ({ fileNo: id })) }
        : processedMsg.file,
  };
};

/**
 * 将用于渲染的 HTML 文本转换为可读的纯文本
 *
 * message.text 是 processTextWithFormats 处理后的 HTML（引用图标、营养/会员卡片等），
 * 直接复制会把 `<span class="ref-tag-icon-img" data-id="B0117"><img src="base64..." /></span>`
 * 之类的 HTML 源码（含 base64 图片）带出去，与页面渲染效果不一致。
 * 此函数将这些结构还原为可读文本：
 *  - 引用图标 span → 移除（不保留任何引用痕迹）
 *  - 卡片中的操作按钮（去填写/开通会员等）→ 移除
 *  - @download / @silvervip / @goldvip 等标记 → 移除
 *  - 其余 HTML 标签 → 剥除，仅保留文本内容
 *  - markdown 加粗/斜体符号 → 还原为纯文本
 *
 * @param {string} [html] - 渲染用 HTML 文本（一般为 message.text）
 * @returns {string} 可读纯文本
 */
export const htmlToPlainText = (html) => {
  if (!html) return "";
  let text = String(html);

  // 1. 移除引用图标（渲染为图片，复制时直接去掉，不保留引用痕迹）
  text = text.replace(
    /<span class="ref-tag-icon-img" data-id="[^"]*"[\s\S]*?<\/span>/gi,
    "",
  );

  // 2. 移除渲染为图标的行内标记（无文本含义，连带其后的空格）
  //    含 @dietRecord / @sportRecord：卡片由消息下方 React 组件渲染，复制时同样不带出该协议码
  text = text
    .replace(/@download\s*/gi, "")
    .replace(/@silvervip\s*/gi, "")
    .replace(/@goldvip\s*/gi, "")
    .replace(/@dietRecord\s*/gi, "")
    .replace(/@sportRecord\s*/gi, "");

  // 3. 换行标签 → 换行符
  text = text.replace(/<br\s*\/?>/gi, "\n");

  // 4. 移除卡片中的操作按钮（"去填写"/"开通会员"等无实义文案）
  text = text.replace(/<button[^>]*>[\s\S]*?<\/button>/gi, "");

  // 5. 剥除剩余 HTML 标签（保留其中的文本内容）
  text = text.replace(/<[^>]*>/g, "");

  // 6. 解码常见 HTML 实体
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // 7. 还原 markdown 格式符号
  text = text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(^|\s)\*([^*\n]+)\*/g, "$1$2")
    .replace(/(^|\s)_([^_\n]+)_/g, "$1$2");

  // 8. 清理多余空白（行尾空格、连续空格、多余空行）
  text = text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/ {2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text;
};

// ============================================================
// 流式消息更新（纯函数）
// ============================================================

/**
 * 根据流式数据构造更新后的消息数组
 *
 * 统一处理思考步骤状态切换与四种文本更新分支，
 * 避免 App.js 中重复的 setter 样板代码。
 *
 * @param {Array} prevMessages - 当前消息数组
 * @param {object} options - 更新参数
 * @param {boolean} options.shouldFinishByThoughtNode
 * @param {boolean} options.hasFinishReason
 * @param {boolean} options.hasRenderableThoughtText
 * @param {object} options.record - 原始数据 record
 * @param {string|null} options.endNodeData
 * @param {string} options.displayText
 * @param {string} options.sessionId
 * @param {string} options.requestId
 * @param {string} [options.recordDate] - 后端下发的记录日期
 * @returns {Array} 更新后的消息数组
 */
export const applyStreamMessageUpdate = (
  prevMessages,
  {
    shouldFinishByThoughtNode,
    hasFinishReason,
    hasRenderableThoughtText,
    record,
    endNodeData,
    displayText,
    sessionId,
    requestId,
    quotaExceeded,
    planCode,
    recordDate,
    dietRecordData,
    sportRecordData,
  },
) => {
  if (!prevMessages?.length) return prevMessages;

  const newMessages = [...prevMessages];
  const lastIndex = newMessages.length - 1;
  const lastMsg = { ...newMessages[lastIndex] };

  // 思考步骤状态更新
  if (shouldFinishByThoughtNode || hasFinishReason) {
    lastMsg.loading = false;
    lastMsg.ending = true;
    lastMsg.thoughtSteps = lastMsg.thoughtSteps?.map((s) => ({
      ...s,
      status: 2,
    }));
  } else if (hasRenderableThoughtText && lastMsg.thoughtSteps) {
    lastMsg.thoughtSteps = lastMsg.thoughtSteps.map((s) => ({
      ...s,
      status: 2,
    }));
  }

  // 统一文本更新
  const updateLast = (rawText, isFinish) => {
    const formatted = processTextWithFormats(
      rawText,
      { ...lastMsg, text: "" },
      isFinish,
    );
  newMessages[lastIndex] = { ...formatted, rawText, sessionId: sessionId || lastMsg.sessionId, requestId: requestId || lastMsg.requestId, quotaExceeded };
  };

  if (hasFinishReason && record?.text) {
    updateLast(record.text, true);
  } else if (hasRenderableThoughtText && endNodeData) {
    const merged = mergeStreamText(lastMsg.rawText || "", endNodeData);
    updateLast(merged, false);
  } else if (displayText) {
    updateLast(displayText, hasFinishReason);
  } else if (shouldFinishByThoughtNode || hasFinishReason) {
    // 纯结束包（无正文内容，如业务返回包 code:400）不更新 requestId，
    // 避免其携带的"业务动作 requestId"覆盖正文消息的 requestId，导致点赞/点踩定位不到消息
    newMessages[lastIndex] = { ...lastMsg, sessionId: sessionId || lastMsg.sessionId, quotaExceeded };
  }

  // 将 planCode 绑定到消息对象（消息级别，支持同一会话多条消息各自下载）
  if (planCode) {
    newMessages[lastIndex] = { ...newMessages[lastIndex], planCode };
  }

  // 将 recordDate 绑定到消息对象
  if (recordDate) {
    newMessages[lastIndex] = { ...newMessages[lastIndex], recordDate };
  }

  // 将结构化卡片数据（dietRecordData/sportRecordData，后端返回的对象字段）绑定到消息对象
  if (dietRecordData && typeof dietRecordData === "object") {
    newMessages[lastIndex] = { ...newMessages[lastIndex], dietRecordData };
  }
  if (sportRecordData && typeof sportRecordData === "object") {
    newMessages[lastIndex] = { ...newMessages[lastIndex], sportRecordData };
  }

  return newMessages;
};

// ===========================================================
// ============================================================
// JSON 缓冲解析
// ============================================================

/**
 * 从流式文本缓冲区中逐段提取并解析 JSON 数据包
 *
 * 流式接口返回的原始文本可能包含多个 JSON 对象，
 * 此函数会从左到右扫描，每找到一个完整 JSON 对象就调用 onPacket 回调。
 *
 * @param {string} buffer - 当前未处理的文本缓冲区
 * @param {boolean} isFinal - 是否为最后一块数据
 * @param {function} onPacket - 每个完整 JSON 数据包的回调 onPacket(parsedData)
 * @returns {string} 剩余无法解析的缓冲区尾部
 */
export const processJSONBuffer = (buffer, isFinal, onPacket) => {
  let position = 0;
  let jsonStart = buffer.indexOf("{", position);
  while (jsonStart !== -1) {
    const endIndex = findMatchingBrace(buffer, jsonStart);
    if (endIndex === -1) break;
    const rawPacket = buffer.substring(jsonStart, endIndex + 1);
    let data;
    try {
      data = JSON.parse(rawPacket);
    } catch (error) {
      console.group(">>> [流式 JSON 解析异常]");
      console.error("Error:", error);
      console.error("Message:", error?.message);
      console.error("Stack:", error?.stack);
      console.log("Packet diagnostics:", {
        isFinal,
        jsonStart,
        endIndex,
        rawPacket,
      });
      console.groupEnd();
      position = endIndex + 1;
      jsonStart = buffer.indexOf("{", position);
      continue;
    }

    try {
      onPacket(data);
    } catch (error) {
      console.group(">>> [流式数据包处理异常]");
      console.error("Error:", error);
      console.error("Message:", error?.message);
      console.error("Stack:", error?.stack);
      console.log("Packet diagnostics:", {
        isFinal,
        data,
        rawPacket,
      });
      console.groupEnd();
    }

    position = endIndex + 1;
    jsonStart = buffer.indexOf("{", position);
  }
  return buffer.substring(position);
};

// ============================================================
// 历史消息格式化（多页面共用）
// ============================================================

/**
 * 将接口返回的历史消息格式化为消息数组
 *
 * 解析附件、构造 user/assistant 消息对，
 * 供 KetogenicDiet 页面和 useHistory 侧边栏共用。
 *
 * @param {Array} historyContent - 接口返回的 data.data.content
 * @param {string} sessionId - 会话 ID
 * @returns {Array} 格式化后的消息数组
 */
export const formatHistoryMessages = (historyContent = [], sessionId) => {
  const formattedMsgs = [];

  historyContent.forEach((item, index) => {
    // 解析图片/文件附件
    let userFileObj = null;
    if (item.additionalInfoId && item.fileType) {
      try {
        const fileLinks = JSON.parse(item.additionalInfoId);
        if (Array.isArray(fileLinks) && fileLinks.length > 0) {
          userFileObj = {
            type: item.fileType,
            list: fileLinks.map((link) => ({ link })),
          };
        }
      } catch (e) {
        console.error(">>> [历史记录] 附件解析失败:", e);
      }
    }

    formattedMsgs.push({
      id: `user_${item.id}_${index}`,
      sender: "user",
      text: item.question || "",
      ending: true,
      hisItem: true,
      sessionId,
      file: userFileObj,
    });

    if (item.response && item.response[0]) {
      const res = item.response[0];
      const processedText = processTextWithFormats(res.content || "", {
        id: item.id,
        hisItem: true,
      });

      formattedMsgs.push({
        ...processedText,
        id: `bot_${item.id}_${index}`,
        sender: "assistant",
        ending: true,
        needTypeWriter: 2,
        sessionId,
        requestId: item.requestId,  // 历史消息自带 requestId（后台已支持），用于点赞/点踩定位消息
        planCode: item.planCode,  // 历史消息自带 planCode（若后端返回），用于消息级下载
        recordDate: item.recordDate||'',  // 历史消息自带 recordDate,用于记录日期
        // 结构化卡片字段（dietRecordData/sportRecordData 等）：历史消息若后端返回则透传，回看时仍可渲染卡片
        dietRecordData: item.dietRecordData,
        sportRecordData: item.sportRecordData,
      });
    }
  });

  return formattedMsgs;
};
